import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Server, Key, Eye, AlertTriangle, CheckCircle2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const SecurityPage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const securityFeatures = [
    {
      icon: Lock,
      title: isRTL ? 'تشفير البيانات' : 'Data Encryption',
      description: isRTL
        ? 'جميع البيانات مشفرة باستخدام AES-256 أثناء التخزين وTLS 1.3 أثناء النقل'
        : 'All data encrypted using AES-256 at rest and TLS 1.3 in transit',
      badge: 'AES-256'
    },
    {
      icon: Server,
      title: isRTL ? 'البنية التحتية الآمنة' : 'Secure Infrastructure',
      description: isRTL
        ? 'مراكز بيانات معتمدة ISO 27001 مع نسخ احتياطي متعدد المواقع'
        : 'ISO 27001 certified data centers with multi-site redundancy',
      badge: 'ISO 27001'
    },
    {
      icon: Key,
      title: isRTL ? 'إدارة الوصول' : 'Access Management',
      description: isRTL
        ? 'مصادقة متعددة العوامل (MFA) والتحكم في الوصول المستند إلى الأدوار (RBAC)'
        : 'Multi-factor authentication (MFA) and role-based access control (RBAC)',
      badge: 'MFA'
    },
    {
      icon: Eye,
      title: isRTL ? 'المراقبة المستمرة' : 'Continuous Monitoring',
      description: isRTL
        ? 'مراقبة أمنية على مدار الساعة طوال أيام الأسبوع مع كشف التهديدات في الوقت الفعلي'
        : '24/7 security monitoring with real-time threat detection',
      badge: '24/7'
    },
    {
      icon: AlertTriangle,
      title: isRTL ? 'الاستجابة للحوادث' : 'Incident Response',
      description: isRTL
        ? 'فريق استجابة مخصص للحوادث الأمنية مع خطط استجابة موثقة'
        : 'Dedicated security incident response team with documented response plans',
      badge: 'SLA'
    },
    {
      icon: Shield,
      title: isRTL ? 'اختبار الاختراق' : 'Penetration Testing',
      description: isRTL
        ? 'اختبارات اختراق منتظمة من قبل جهات خارجية معتمدة'
        : 'Regular penetration testing by certified third parties',
      badge: 'Annual'
    }
  ];

  const certifications = [
    { name: 'SOC 2 Type II', status: isRTL ? 'معتمد' : 'Certified' },
    { name: 'GDPR', status: isRTL ? 'متوافق' : 'Compliant' },
    { name: 'ISO 27001', status: isRTL ? 'معتمد' : 'Certified' },
    { name: 'PCI DSS', status: isRTL ? 'متوافق' : 'Compliant' }
  ];

  return (
    <div className={`min-h-screen bg-background ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Link to="/">
              <Button variant="ghost" className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <ArrowLeft className="h-4 w-4" />
                {isRTL ? 'العودة للرئيسية' : 'Back to Home'}
              </Button>
            </Link>
            <h1 className="text-xl font-bold">{isRTL ? 'الأمان' : 'Security'}</h1>
            <div className="w-32" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 mb-4">
            <Shield className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold mb-4">{isRTL ? 'الأمان والامتثال' : 'Security & Compliance'}</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {isRTL 
              ? 'نحن ملتزمون بحماية بياناتك بأعلى معايير الأمان في الصناعة'
              : 'We are committed to protecting your data with industry-leading security standards'
            }
          </p>
        </div>

        {/* Certifications */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <h3 className="font-semibold text-lg mb-4 text-center">
              {isRTL ? 'الشهادات والامتثال' : 'Certifications & Compliance'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {certifications.map((cert, index) => (
                <div key={index} className="text-center p-4 rounded-lg bg-muted/50">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                  <div className="font-medium">{cert.name}</div>
                  <Badge variant="secondary" className="mt-1">{cert.status}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Security Features */}
        <div className="grid md:grid-cols-2 gap-6">
          {securityFeatures.map((feature, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                    <feature.icon className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className={`flex-1 ${isRTL ? 'text-right' : ''}`}>
                    <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                      <h3 className="font-semibold">{feature.title}</h3>
                      <Badge variant="outline" className="text-xs">{feature.badge}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Contact */}
        <Card className="mt-8 bg-muted/50">
          <CardContent className="p-6 text-center">
            <Mail className="h-8 w-8 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-semibold mb-2">{isRTL ? 'الإبلاغ عن مشكلة أمنية' : 'Report a Security Issue'}</h3>
            <p className="text-muted-foreground mb-4">
              {isRTL ? 'تواصل مع فريق الأمان لدينا على' : 'Contact our security team at'}
            </p>
            <a href="mailto:feed@maxefeed.com" className="text-primary hover:underline font-medium">
              feed@maxefeed.com
            </a>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SecurityPage;
